function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
// ----------------------------------------------------------------------------
// Web Component: iamCartItems <iam-cart-item>
// Extends: HTMLElement
// ----------------------------------------------------------------------------
// TODO
// ----------------------------------------------------------------------------
// Customization:
// - TODO
// ----------------------------------------------------------------------------
// Event Listeners:
// - `formValidation:init`            → Initializes form validation when the validation
//                                      class is ready
// - `cartForm:changed`            → Listens for when the form data changes
//                                      (option, selected variant, quantity, property)
//                                      to update the form validation state
// ----------------------------------------------------------------------------
// Event Triggers:
// - `cartForm:init`               → Triggers when the product form is ready
//                                      for modules/extensions to hook in
// - `cartForm:validation:init`    → Triggers when the form validation is initialized
//                                      (allowing modules to extend validation)
// - `cartForm:validation:changed` → Triggers when the form validation state changes
// - `cartForm:validation:success` → Triggers when the form validation is successful
// - `cartForm:validation:failed`  → Triggers when the form validation fails
// - `cartForm:changed`            → Triggers when the form data changes
//                                      (any of option, selected variant, quantity, property)
// - `cartForm:option:changed`     → Triggers when the form data changes (option)
// - `cartForm:variant:changed`    → Triggers when the form data changes (selected variant)
// - `cartForm:quantity:changed`   → Triggers when the form data changes (quantity)
// - `cartForm:property:changed`   → Triggers when the form data changes (property)
// - `cartForm:submitting`         → Triggers when the form is being submitted
// - `cartForm:submitted:success`  → Triggers when the form is successfully submitted
// - `cartForm:submitted:error`    → Triggers when the form submission fails
// ----------------------------------------------------------------------------
// Event Filters:
// - `cartForm:submitting:data`    → Allows filtering of the form data before submission
// ----------------------------------------------------------------------------
// Key Data Mutations:
// - Fetches product and custom data (product-form.graphql) and initializes the Shopify `cartForm`
// - Toggles form loading and error states based on child components and validation states
// - Submits the form over AJAX after validation
// ----------------------------------------------------------------------------
// Gotchas:
// - Relies on promises to fetch product and custom data, which may fail if the API calls do not resolve successfully.
// - Custom product data is only fetched if a query is provided.
// - Error messages are injected into the `.form__errors-container` element
//   and the form is marked with a `--error` class until resolved
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Helpers
import iamFormElement from 'scripts/classes/iamFormElement';
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import { sectionRendering } from 'scripts/helpers/iamota-shopify-ajax';
import translate from 'scripts/helpers/iamota-translate';

// Elements
import '../cart-item/cart-item';
import Debug from 'scripts/libs/debug';

// -------------------------------------
// Web Component
// -------------------------------------

class iamCartItems extends iamFormElement {
  get namespace() {
    return "".concat(this.formName, ":items");
  }
  get cartItems() {
    var _this$cartItemsContai;
    return (_this$cartItemsContai = this.cartItemsContainer) === null || _this$cartItemsContai === void 0 ? void 0 : _this$cartItemsContai.querySelectorAll('iam-cart-item');
  }

  /**
   * Initialize the component when the product form is initialized
   */
  onFormInit() {
    // Variables
    this.cartItemsContainer = this.querySelector('.cart__items');

    // Validation
    if (!this.validateRequiredAttributes(['cartItemsContainer'])) return;

    // Bind Event Listeners
    iamotaEventHandler.subscribe('cart:modified', this.onCartModified.bind(this), {
      source: "cart-items.js"
    }, {}, 1000);

    // Trigger Events
    iamotaEventHandler.trigger('cartForm:items:init', {
      iamCartItems: this
    }, {
      context: this.iamForm,
      isAtomic: true
    });
  }
  onCartModified(cart, additionalData) {
    // Remove cart items that are no longer in the cart
    this.cartItems.forEach((cartItem, index) => {
      var _cartItem$itemData;
      var itemKey = cartItem === null || cartItem === void 0 || (_cartItem$itemData = cartItem.itemData) === null || _cartItem$itemData === void 0 ? void 0 : _cartItem$itemData.key;
      if (!cart.items.some(cartItem => cartItem.key === itemKey)) {
        Debug.extreme('Cart modfied detected an item that no longer exists in the cart, removing from DOM', {
          cartItem,
          index,
          cartItems: this.cartItems,
          cart,
          additionalData
        });
        cartItem.remove();
      }
    });

    // Other than simple removals handled above:
    // If the cart materially changes, refresh the whole cart
    if (cart.items.length === 0 || cart.items.length !== this.cartItems.length || cart.items.some((item, index) => {
      var _this$cartItems$index;
      return item.key !== ((_this$cartItems$index = this.cartItems[index]) === null || _this$cartItems$index === void 0 || (_this$cartItems$index = _this$cartItems$index.itemData) === null || _this$cartItems$index === void 0 ? void 0 : _this$cartItems$index.key);
    })) {
      Debug.log('Cart items have materially changed, refreshing all cart items to resync', {
        cartItems: this.cartItems,
        cart,
        additionalData
      });
      this.refreshCartItems();
    }
  }
  refreshCartItems() {
    var _this = this;
    this.setLoading(true);
    sectionRendering.fetchSingle('main-cart__items').then(/*#__PURE__*/function () {
      var _ref = _asyncToGenerator(function* (sectionContent) {
        var cartItemHTML = yield iamotaEventHandler.applyFilters('cartForm:items:updating', sectionContent);
        _this.cartItemsContainer.innerHTML = cartItemHTML;
        iamotaEventHandler.trigger('cartForm:items:updated', {
          cartItemHTML
        }, {
          context: _this
        });
      });
      return function (_x) {
        return _ref.apply(this, arguments);
      };
    }()).then(() => {
      var _this$iamForm;
      // Revalidate the new form fields
      (_this$iamForm = this.iamForm) === null || _this$iamForm === void 0 || (_this$iamForm = _this$iamForm.validator) === null || _this$iamForm === void 0 || _this$iamForm.revalidate();
    }).catch(error => {
      console.error('cartForm: Error fetching main-cart__items section:', error);
      this.cartItemsContainer.innerHTML = translate('An unexpected error occurred when updating the cart. Please try reloading the page.', 'cartForm.error_retrieving_cart_items');
    }).finally(() => {
      this.setLoading(false);
    });
  }

  /**
   * Initialize the the form validator
   * @param {} param
   */
  onValidationInit(_ref2) {
    var {
      validator
    } = _ref2;
    this.validator = validator;
  }
}
customElements.define('iam-cart-items', iamCartItems);